import './header';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { getTemplate } from '@automator/features/page-template-library/utilities';

// Import global utils
import { openWindow } from '@automator/shared/utils/open-window';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-library-template' ) // eslint-disable-line require-jsdoc
export class AutomatorLibraryTemplate extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The template ID
	 *
	 * @type {Number} The ID of the template
	 */
	@property( { type: Number, attribute: 'template-id' } )
	accessor templateID;

	/**
	 * A flag to enable standalone mode for the template. This mode is intended for cases where the template library is used in an external environment, such as a site other than the one where recipes are intended to be imported.
	 *
	 * When `true`, the following changes occur:
	 * - Dependency checks are limited: Missing dependencies are displayed without options to "install" or "connect."
	 * - The "Import Recipe" button is hidden, as recipes cannot be installed directly from an external site.
	 *
	 * By default, this flag is set to `false`, meaning the template assumes it is running within its native environment.
	 *
	 * @type {Boolean} The standalone mode flag
	 */
	@property( { type: Boolean, attribute: 'standalone-mode' } )
	accessor standaloneMode = false;

	/**
	 * Renders the component
	 *
	 * @return {TemplateResult|String|undefined} The template
	 */
	render() {
		// Check if the ID is defined
		if ( isEmpty( this.templateID ) ) {
			return;
		}

		// Check if the template data exists
		if ( isEmpty( this.directoryData ) ) {
			return sprintf(
				escapeHTML( __( 'Template ID %1$s not found', 'uncanny-automator' ) ),
				this.templateID
			);
		}

		return this._templateMain;
	}

	/**
	 * Returns the main template, containing all parts
	 *
	 * @return {TemplateResult} The main template
	 */
	get _templateMain() {
		return html`
			<div class="template">
				<div class="template-body">
					${ this._templateHeader }
					${ this._templateTriggers }
					${ this._templateActions }
				</div>

				${ this._templateSidebar }
			</div>
		`;
	}

	/**
	 * Returns the template of the header of the dialog. This shows:
	 * 1. Recipe name
	 * 2. Recipe description
	 * 3. User type
	 *
	 * @return {TemplateResult} The header
	 */
	get _templateHeader() {
		// Create summary array
		const recipeSummary = [];

		// Add the recipe type
		recipeSummary.push( {
			key:   html`
				${ escapeHTML( __( 'User type', 'uncanny-automator' ) ) }
				
				<uo-tooltip
					class="sidebar-header-close-dialog"
				>
					${ escapeHTML( __( 'Specifies which user group can trigger this recipe, such as logged-in users or all users.', 'uncanny-automator' ) ) }

					<uo-button 
						slot="target"
						color="transparent"
						size="small"

						@click=${ () => {
							openWindow( {
								url: 'https://automatorplugin.com/knowledge-base/creating-a-recipe/?utm_source=uncanny_automator_pro&utm_medium=publish_metabox&utm_content=recipe_type_question_icon',
								w: 450
							} );
						} }
					>
						<uo-icon id="circle-question"></uo-icon>
					</uo-button>
				</uo-tooltip>
			`,
			value: this.directoryData.recipe_type === 'user'
				? escapeHTML( __( 'Logged-in users', 'uncanny-automator' ) )
				: escapeHTML( __( 'Everyone', 'uncanny-automator' ) ),
		} );

		return html`
			<uap-template-header
				recipe-title=${ this.directoryData.title }
				recipe-description=${ this.directoryData.description }
				?requires-pro=${ this.directoryData.requires_pro }
				?requires-elite=${ this.directoryData.requires_elite }
				.summary=${ recipeSummary }
			></uap-template-header>
		`;
	}

	/**
	 * Returns the template that shows the list of triggers
	 *
	 * @return {TemplateResult} The triggers section
	 */
	get _templateTriggers() {
		return html`
			<div class="template-triggers">
				<h2 class="template-section-title">
					${ escapeHTML( __( 'Triggers', 'uncanny-automator' ) ) }
					${ this.directoryData.triggers.items.length > 1
						? html`
							<uo-chip filled>
								${ this.directoryData.triggers.logic === 'ANY'
									? escapeHTML( __( 'Any', 'uncanny-automator' ) )
									: escapeHTML( __( 'All', 'uncanny-automator' ) )
								}
							</uo-chip>
						`
						: ''
					}
				</h2>

				<div class="template-section-items">
					${ this._templateTriggersItems }
				</div>
			</div>
		`;
	}

	/**
	 * Returns the template that shows the list of triggers
	 *
	 * @return {TemplateResult} The trigger items
	 */
	get _templateTriggersItems() {
		if ( isEmpty( this.directoryData.triggers.items ) ) {
			return html`
				<em>${ escapeHTML( __( 'When something happens...', 'uncanny-automator' ) ) }</em>
			`;
		}

		return repeat(
			this.directoryData.triggers.items,
			( item, index ) => index,
			item => {
				return html`
					<uap-template-item-trigger
						integration-code=${ item.integration_code }
						.title=${ item.title }
						.titleHTML=${ item.title_html }

						?is-pro=${ item.is_pro }
						?is-elite=${ item.is_elite }
					></uap-template-item-trigger>
				`;
			}
		);
	}

	/**
	 * Returns the template that shows the list of actions, filters and loops
	 *
	 * @return {TemplateResult} The actions section. This includes filters and loops too
	 */
	get _templateActions() {
		return html`
			<div class="template-actions">
				<h2 class="template-section-title">${ escapeHTML( __( 'Actions', 'uncanny-automator' ) ) }</h2>

				<div class="template-section-items">
					${ this._templateActionsItems }
				</div>
			</div>
		`;
	}

	/**
	 * Returns the template that shows the list of actions, filters and loops
	 *
	 * @return {TemplateResult} The actions items
	 */
	get _templateActionsItems() {
		if ( isEmpty( this.directoryData.actions.items ) ) {
			return html`
				<em>${ escapeHTML( __( 'Do something...', 'uncanny-automator' ) ) }</em>
			`;
		}

		return repeat(
			this.directoryData.actions.items,
			( item, index ) => index,
			item => {
				// Check what element we have to render
				if ( item.type === 'action' ) {
					return html`
						<uap-template-item-action 
							integration-code=${ item.integration_code }
							.title=${ item.title }
							.titleHTML=${ item.title_html }
							.delay=${ item.delay }

							?is-pro=${ item.is_pro }
							?is-elite=${ item.is_elite }
						></uap-template-item-action>`;
				}

				if ( item.type === 'filter' ) {
					return html`
						<uap-template-filter 
							logic="${ item.logic }"
							.conditions=${ item.conditions }
							.items=${ item.items }
						></uap-template-filter>
					`;
				}

				if ( item.type === 'loop' ) {
					return html`
						<uap-template-loop 
							logic=${ item.logic }
							.loopFilters=${ item.loops_filters }
							.items=${ item.items }
							.iterableExpression=${ item.iterable_expression }
						></uap-template-loop>
					`;
				}
			}
		);
	}

	/**
	 * Returns the template that shows the sidebar
	 *
	 * @return {TemplateResult|undefined} The sidebar
	 */
	get _templateSidebar() {
		return html`
			<div class="template-sidebar">
				<uap-template-sidebar
					template-id=${ this.templateID }
					.requirements=${ this.directoryData.requirements }
					?standalone-mode=${ this.standaloneMode }
				></uap-template-sidebar>
			</div>
		`;
	}

	/**
	 * Retrieves the data for the template directory.
	 *
	 * @return {Object} The directory data object for the current template ID.
	 */
	get directoryData() {
		return getTemplate( this.templateID );
	}
}
